import { parse } from './urlHelpers';

const envRoot = (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').trim().replace(/\/$/, '');
const FALLBACK_ORIGIN =
  envRoot || (typeof window !== 'undefined' && window.location?.origin) || 'http://localhost';

export const LISTING_BACK_CONTEXTS = {
  favorites: {
    key: 'favorites',
    messageId: 'ListingPage.backToFavorites',
    name: 'FavoritesPage',
    search: '',
    referrerPaths: ['/favorites'],
  },
  browse: {
    key: 'browse',
    messageId: 'ListingPage.backToListeners',
    name: 'SearchPage',
    search: '?availableNow=0',
    referrerPaths: ['/s'],
  },
  inbox: {
    key: 'inbox',
    messageId: 'ListingPage.backToMyBookings',
    name: 'MyBookingsPage',
    search: '',
    referrerPaths: ['/bookings', '/inbox'],
  },
  dashboard: {
    key: 'dashboard',
    messageId: 'ListingPage.backToDashboard',
    name: 'DashboardPage',
    search: '',
    referrerPaths: ['/dashboard'],
  },
};

const FROM_ALIASES = {
  favorites: 'favorites',
  favorite: 'favorites',
  browse: 'browse',
  listeners: 'browse',
  search: 'browse',
  inbox: 'inbox',
  bookings: 'inbox',
  'my-bookings': 'inbox',
  dashboard: 'dashboard',
};

/**
 * Map a raw from= / state.from / alias to a listing-back context key.
 *
 * @param {unknown} raw
 * @returns {'favorites'|'browse'|'inbox'|'dashboard'|null}
 */
export const normalizeListingBackContext = raw => {
  if (raw == null) {
    return null;
  }
  const key = String(raw)
    .trim()
    .toLowerCase();
  return FROM_ALIASES[key] || null;
};

/**
 * NamedLink `to` that stamps how the talker opened a listing.
 *
 * @param {unknown} context
 * @returns {{ search?: string, state?: { from: string } }}
 */
export const listingPageFromTo = context => {
  const key = normalizeListingBackContext(context);
  if (!key) {
    return {};
  }
  const backSearch =
    key === 'browse' && typeof window !== 'undefined' && window.location?.pathname?.startsWith('/s')
      ? window.location.search || ''
      : undefined;
  return {
    search: `?from=${key}`,
    state: backSearch !== undefined ? { from: key, backSearch } : { from: key },
  };
};

const pathMatches = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * Infer favorites / browse / inbox / dashboard from a same-app referrer URL.
 *
 * @param {string} [referrer]
 * @returns {'favorites'|'browse'|'inbox'|'dashboard'|null}
 */
export const inferListingBackContextFromReferrer = referrer => {
  if (!referrer || typeof referrer !== 'string') {
    return null;
  }
  let url;
  try {
    url = new URL(referrer, FALLBACK_ORIGIN);
  } catch (e) {
    return null;
  }
  const path = url.pathname || '';
  if (pathMatches(path, '/favorites')) {
    return 'favorites';
  }
  if (path === '/s' || path.startsWith('/s/')) {
    return 'browse';
  }
  if (pathMatches(path, '/bookings') || pathMatches(path, '/inbox')) {
    return 'inbox';
  }
  if (pathMatches(path, '/dashboard')) {
    if ((url.hash || '') === '#favorites') {
      return 'favorites';
    }
    return 'dashboard';
  }
  return null;
};

/**
 * Prefer ?from= and history.state, then the document referrer.
 *
 * @param {Object} [input]
 * @param {string|Object} [input.search]
 * @param {Object} [input.state]
 * @param {string} [input.referrer]
 * @returns {'favorites'|'browse'|'inbox'|'dashboard'|null}
 */
export const inferListingBackContext = ({ search, state, referrer } = {}) => {
  const fromSearch =
    typeof search === 'string'
      ? parse(search).from
      : search && typeof search === 'object'
      ? search.from
      : null;
  const fromQuery = normalizeListingBackContext(fromSearch);
  if (fromQuery) {
    return fromQuery;
  }
  const fromState = normalizeListingBackContext(state?.from);
  if (fromState) {
    return fromState;
  }
  return inferListingBackContextFromReferrer(referrer);
};

/**
 * Named-route fallback for a listing-back context.
 *
 * @param {unknown} context
 * @returns {typeof LISTING_BACK_CONTEXTS[keyof typeof LISTING_BACK_CONTEXTS]|null}
 */
export const listingBackDestination = context => {
  const key = normalizeListingBackContext(context);
  return key ? LISTING_BACK_CONTEXTS[key] : null;
};

/**
 * @param {string} [referrer]
 * @param {string} [origin]
 * @returns {boolean}
 */
export const isSameOriginReferrer = (referrer, origin) => {
  if (!referrer || !origin) {
    return false;
  }
  try {
    return new URL(referrer).origin === new URL(origin).origin;
  } catch (e) {
    return false;
  }
};

/**
 * @param {string} [referrer]
 * @param {unknown} context
 * @returns {boolean}
 */
export const referrerMatchesListingBack = (referrer, context) => {
  const dest = listingBackDestination(context);
  if (!dest || !referrer) {
    return false;
  }
  try {
    const url = new URL(referrer, FALLBACK_ORIGIN);
    const path = url.pathname || '';
    return dest.referrerPaths.some(prefix => pathMatches(path, prefix));
  } catch (e) {
    return false;
  }
};

/**
 * Use history.back() when the previous same-origin page is the inferred place.
 * With no inferred context, any same-origin history entry is enough.
 *
 * @param {Object} input
 * @param {string} [input.referrer]
 * @param {string} [input.origin]
 * @param {unknown} [input.context]
 * @param {number} [input.historyLength]
 * @returns {boolean}
 */
export const shouldHistoryBackToContext = ({
  referrer,
  origin,
  context,
  historyLength,
} = {}) => {
  if (!(historyLength > 1)) {
    return false;
  }
  if (!isSameOriginReferrer(referrer, origin)) {
    return false;
  }
  if (!context) {
    return true;
  }
  return referrerMatchesListingBack(referrer, context);
};
